import { AccordionDetails, AccordionSummary, Box, styled } from '@mui/material';
import FlexBox from 'Components/FlexBox';
import { ThemeTypography } from 'DesignSystem';
import { Accordion } from 'DesignSystem/DataDisplay/Accordion/Accordion';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { Badge } from 'FeatureModules/DataImport/Components/DataImport.styles';
import React, { FC } from 'react';
import { TGeoDataIssueCode } from '../types/dataset.types';

export interface IIssue {
  code: TGeoDataIssueCode;
  message: string;
  count: number;
}

interface PlotIssueBannerProps {
  issues: IIssue[];
  issueCount: number;
  severity: 'warning' | 'error' | 'info';
}

const Container = styled(FlexBox)<{ severity: 'warning' | 'error' | 'info' }>(
  ({ theme, severity }) => ({
    gap: theme.spacing(2),
    background:
      severity === 'error'
        ? theme.custom.themeColors.error[20]
        : severity === 'warning'
        ? theme.custom.themeColors.accent[20]
        : theme.custom.themeColors.primary[10],
    borderRadius: theme.spacing(1),
    height: 'fit-content',
  })
);

const NumberCircle = styled(Box)<{ severity: 'warning' | 'error' | 'info' }>(
  ({ theme, severity }) => ({
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    background:
      severity === 'error'
        ? theme.custom.themeColors.error[60]
        : severity === 'warning'
        ? theme.custom.themeColors.accent[60]
        : theme.custom.themeColors.primary[60],
  })
);

const StyledAccordion = styled(Accordion)(({ theme, transparent }) => ({
  '&.MuiAccordion-root': {
    margin: 0,
    border: 'none !important',
  },

  '& .MuiAccordionDetails-root': {
    paddingTop: 0,
    background: 'transparent',
  },
}));

const PlotIssueBanner: FC<PlotIssueBannerProps> = ({ issues, issueCount, severity }) => {
  return (
    <Container severity={severity}>
      <StyledAccordion transparent={true}>
        <AccordionSummary>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon
              name="warning"
              color={severity === 'error' ? 'red-80' : 'yellow-80'}
              size="x-large"
            />
            <Badge severity={severity}>
              <ThemeTypography variant="BODY_SMALL_BOLD" color="ALMOST_BLACK">
                {issueCount} plot {severity}
                {issueCount > 1 && 's'}
              </ThemeTypography>
            </Badge>

            <ThemeTypography color="ALMOST_BLACK" variant="BODY_MEDIUM_BOLD">
              {severity === 'error'
                ? 'Faulty plot geometries detected (This prevents a credible deforestation analysis)'
                : 'Potential geometry issues detected (Review and correct if needed, or proceed if data is accurate)'}
            </ThemeTypography>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Box display="flex" flexDirection="column">
            {issues
              // Sort by warning count
              .sort((a, b) => b.count - a.count)
              .map(({ code, message, count }, index) => (
                <Box key={`${code}-${index}`}>
                  <FlexBox mt={1} gap={1}>
                    <NumberCircle severity={severity}>
                      <ThemeTypography variant="BODY_SMALL_BOLD" color="ALMOST_BLACK">
                        {count}
                      </ThemeTypography>
                    </NumberCircle>
                    <ThemeTypography color="ALMOST_BLACK" variant="BODY_SMALL">
                      {message}
                    </ThemeTypography>
                  </FlexBox>
                </Box>
              ))}
          </Box>
        </AccordionDetails>
      </StyledAccordion>
    </Container>
  );
};

export default PlotIssueBanner;
